import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, Plus, Edit2, Trash2, MapPin, Shield, Key } from 'lucide-react';
import axios from '../lib/axios';
import Layout from '../components/Layout';

// Render a UTC timestamp (ISO-8601 or legacy "YYYY-MM-DD HH:MM:SS") in Dubai local time.
function fmtTime(utc) {
  if (!utc) return 'never';
  const s = String(utc);
  const d = new Date(s.endsWith('Z') ? s : s.replace(' ', 'T') + 'Z');
  if (isNaN(d.getTime())) return utc;
  return d.toLocaleString('en-GB', { timeZone: 'Asia/Dubai', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
}

export default function UserManagement() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showLocationModal, setShowLocationModal] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'user' });
  const [passwordForm, setPasswordForm] = useState('');
  const [locationForm, setLocationForm] = useState({ locationId: '', role: 'viewer' });

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const { data } = await axios.get('/api/users');
      return data;
    },
  });

  const { data: locationsData } = useQuery({
    queryKey: ['all-locations'],
    queryFn: async () => {
      const { data } = await axios.get('/api/locations');
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (userData) => axios.post('/api/users', userData),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-users']);
      setShowCreateModal(false);
      setForm({ email: '', name: '', password: '', role: 'user' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...updates }) => axios.put(`/api/users/${id}`, updates),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-users']);
      setEditingUser(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => axios.delete(`/api/users/${id}`),
    onSuccess: () => queryClient.invalidateQueries(['admin-users']),
  });

  const passwordMutation = useMutation({
    mutationFn: ({ id, password }) => axios.put(`/api/users/${id}/password`, { password }),
    onSuccess: () => {
      setShowPasswordModal(null);
      setPasswordForm('');
    },
  });

  const assignLocationMutation = useMutation({
    mutationFn: ({ userId, locationId, role }) => axios.post(`/api/users/${userId}/locations`, { locationId: parseInt(locationId, 10), role }),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-users']);
      setShowLocationModal(null);
      setLocationForm({ locationId: '', role: 'viewer' });
    },
  });

  const removeLocationMutation = useMutation({
    mutationFn: ({ userId, locationId }) => axios.delete(`/api/users/${userId}/locations/${locationId}`),
    onSuccess: () => queryClient.invalidateQueries(['admin-users']),
  });

  const users = data?.users || [];
  const locations = locationsData?.locations || [];

  return (
    <Layout>
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-primary to-brand-secondary flex items-center justify-center text-white">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-brand-dark">User Management</h1>
                <p className="text-sm text-brand-neutral">{users.length} users</p>
              </div>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" /> Add User
            </button>
          </div>

          {/* Users List */}
          {isLoading ? (
            <div className="text-center py-12 text-brand-neutral">Loading users...</div>
          ) : (
            <div className="space-y-3">
              {users.map(u => (
                <div key={u.id} className="bg-white rounded-xl border border-brand-light/60 p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-brand-dark">{u.name || u.email}</span>
                        {u.role === 'super_admin' && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full uppercase">Super Admin</span>
                        )}
                      </div>
                      <p className="text-sm text-brand-neutral mt-0.5">{u.email}</p>
                      <p className="text-[11px] text-brand-neutral mt-1">
                        Last login: <span className="text-brand-dark font-medium">{fmtTime(u.last_login)}</span>
                        <span className="mx-1.5 text-brand-light">·</span>
                        Last active: <span className="text-brand-dark font-medium">{fmtTime(u.last_active)}</span>
                      </p>
                      {/* Location assignments */}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(u.locations || []).map(loc => (
                          <span key={loc.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand-light/50 text-brand-dark text-[11px] rounded-full">
                            <MapPin className="w-3 h-3" />
                            {loc.name}
                            <span className="text-brand-neutral">({loc.role})</span>
                            <button
                              onClick={() => removeLocationMutation.mutate({ userId: u.id, locationId: loc.id })}
                              className="ml-0.5 text-red-400 hover:text-red-600"
                              title="Remove"
                            >&times;</button>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setShowLocationModal(u)}
                        className="p-1.5 text-brand-neutral hover:text-brand-primary hover:bg-brand-light/60 rounded-lg transition-colors"
                        title="Assign Location"
                      >
                        <MapPin className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setShowPasswordModal(u)}
                        className="p-1.5 text-brand-neutral hover:text-brand-primary hover:bg-brand-light/60 rounded-lg transition-colors"
                        title="Reset Password"
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingUser(u);
                          setForm({ email: u.email, name: u.name || '', role: u.role });
                        }}
                        className="p-1.5 text-brand-neutral hover:text-brand-primary hover:bg-brand-light/60 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Deactivate user ${u.email}?`)) {
                            deleteMutation.mutate(u.id);
                          }
                        }}
                        className="p-1.5 text-brand-neutral hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Deactivate"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Create / Edit Modal */}
          {(showCreateModal || editingUser) && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
                <h2 className="text-lg font-bold text-brand-dark mb-4">
                  {editingUser ? 'Edit User' : 'Create User'}
                </h2>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  if (editingUser) {
                    updateMutation.mutate({ id: editingUser.id, name: form.name, role: form.role });
                  } else {
                    createMutation.mutate(form);
                  }
                }} className="space-y-3">
                  <input
                    type="email"
                    placeholder="Email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    disabled={!!editingUser}
                    className="w-full px-3 py-2 border border-brand-light rounded-lg text-sm disabled:bg-gray-50"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Name"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-brand-light rounded-lg text-sm"
                  />
                  {!editingUser && (
                    <input
                      type="password"
                      placeholder="Password"
                      value={form.password}
                      onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                      className="w-full px-3 py-2 border border-brand-light rounded-lg text-sm"
                      required
                    />
                  )}
                  <select
                    value={form.role}
                    onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                    className="w-full px-3 py-2 border border-brand-light rounded-lg text-sm"
                  >
                    <option value="user">User</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                  <div className="flex gap-2 pt-2">
                    <button type="submit" className="flex-1 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-primary/90">
                      {editingUser ? 'Save' : 'Create'}
                    </button>
                    <button type="button" onClick={() => { setShowCreateModal(false); setEditingUser(null); }} className="flex-1 py-2 bg-gray-100 text-brand-dark rounded-lg text-sm font-medium hover:bg-gray-200">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Password Reset Modal */}
          {showPasswordModal && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
                <h2 className="text-lg font-bold text-brand-dark mb-4">Reset Password</h2>
                <p className="text-sm text-brand-neutral mb-3">{showPasswordModal.email}</p>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  passwordMutation.mutate({ id: showPasswordModal.id, password: passwordForm });
                }}>
                  <input
                    type="password"
                    placeholder="New password"
                    value={passwordForm}
                    onChange={e => setPasswordForm(e.target.value)}
                    className="w-full px-3 py-2 border border-brand-light rounded-lg text-sm mb-3"
                    required
                    minLength={6}
                  />
                  <div className="flex gap-2">
                    <button type="submit" className="flex-1 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium">Reset</button>
                    <button type="button" onClick={() => setShowPasswordModal(null)} className="flex-1 py-2 bg-gray-100 rounded-lg text-sm font-medium">Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Assign Location Modal */}
          {showLocationModal && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
                <h2 className="text-lg font-bold text-brand-dark mb-4">Assign Location</h2>
                <p className="text-sm text-brand-neutral mb-3">{showLocationModal.email}</p>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  assignLocationMutation.mutate({ userId: showLocationModal.id, ...locationForm });
                }} className="space-y-3">
                  <select
                    value={locationForm.locationId}
                    onChange={e => setLocationForm(f => ({ ...f, locationId: e.target.value }))}
                    className="w-full px-3 py-2 border border-brand-light rounded-lg text-sm"
                    required
                  >
                    <option value="">Select location...</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                  <select
                    value={locationForm.role}
                    onChange={e => setLocationForm(f => ({ ...f, role: e.target.value }))}
                    className="w-full px-3 py-2 border border-brand-light rounded-lg text-sm"
                  >
                    <option value="viewer">Viewer</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                  <div className="flex gap-2 pt-1">
                    <button type="submit" className="flex-1 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium">Assign</button>
                    <button type="button" onClick={() => setShowLocationModal(null)} className="flex-1 py-2 bg-gray-100 rounded-lg text-sm font-medium">Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
